import {
  RecipeTemplateDto,
  RecipeTemplateGroupDto,
  RecipeTemplateColumnDto,
  RecipeTemplateRowDto,
  RecipeGroupDto,
  RecipeRowDto,
} from '../../api/ApiClient';

// Re-export API types for convenience
export type {
  RecipeTemplateDto,
  RecipeTemplateGroupDto,
  RecipeTemplateColumnDto,
  RecipeTemplateRowDto,
  RecipeGroupDto,
  RecipeRowDto,
};

// Column types - matching the original Knockout implementation
export enum ColumnType {
  Number = 'N',
  Scientific = 'Sci',
  Text = 'S',
  DateTime = 'D',
  List = 'L',
  MultiSelect = 'ML',
  OptionalList = 'OL',
  ExtendibleList = 'EL',
  Lookup = 'U',
  /** HTML content - renders HTML markup instead of plain text */
  HTML = 'H',
}

export enum Source {
  Manual = 'M',
  Formula = 'F',
  OverwrittableFormula = 'OF',
  Constant = 'C',
  Autoincrement = 'A',
  FormulaPerRow = 'FR',
  FormulaAfterUpload = 'FU',
  Fileupload = 'U',
  /**
   * References a field from a sample startingMaterial.
   * The column's 'source' property specifies the field path (e.g., "sampleLabel", "materials").
   * For fixed startingMaterial references, use 'startingMaterialIndex' (1-based).
   * For dynamic startingMaterial binding (rows bound to startingMaterials), the row index maps to startingMaterial index.
   */
  StartingMaterial = 'P',
}

export enum Visibility {
  Always = 'A',
  OnlyForUpload = 'U',
  Never = 'N',
  IfCondition = 'C',
  IfModifiable = 'M',
  IfReadonly = 'R',
}

export enum VisibilityOperator {
  EQ = 'eq',
  NE = 'ne',
  IN = 'in',
  NOT_IN = 'not_in',
  CONTAINS = 'contains',
}

// Row types
export enum RowType {
  /**
   * Only one row is used. No rows can be added / removed
   */
  One = '1',
  /**
   * Rows are defined in the template, no other rows can be added / removed later
   */
  Template = 'T',
  /**
   * User can add or remove rows
   */
  Manual = 'M',
  /**
   * File upload adds rows. User can add/remove after uploading.
   */
  Fileupload = 'F',
  /**
   * Rows are automatically created based on sample startingMaterials.
   * Each row corresponds to one startingMaterial (row 0 = startingMaterial 1, row 1 = startingMaterial 2, etc.).
   * Rows cannot be added or removed manually.
   * Columns with Source.StartingMaterial will automatically reference the corresponding startingMaterial.
   */
  StartingMaterials = 'PR',
}

export enum CommentMode {
  OFF = 'off',
  ON = 'on',
  COLLAPSED = 't',
  READONLY = 'r',
}

// React state types
export interface RecipeColumnState {
  id?: number;
  symbol: string;
  name: string;
  unit: string;
  type: ColumnType;
  defaultValue?: string;
  precision?: number;
  sourceType: Source;
  source?: string;
  visibility: Visibility;
  visibilityFormula?: string;
  refColumn?: string;
  visibilityOp?: VisibilityOperator;
  visibilityVal?: string;
  updateVisibilities?: boolean;
  values?: any[];
  lookupValues?: string;
  color?: string;
  width?: string;

  /**
   * For Source.StartingMaterial: specifies which startingMaterial to reference (1-based index).
   * If undefined, the row index is used (for dynamic startingMaterial binding).
   */
  startingMaterialIndex?: number;

  // Computed properties
  visible: boolean;
  readonly: boolean;
  isNumber: boolean;
  isScientific: boolean;
  isText: boolean;
  isHTML: boolean;
  isDateTime: boolean;
  isList: boolean;
  isMultiSelect: boolean;
  isOptionalList: boolean;
  isExtendibleList: boolean;
  isLookup: boolean;
  useOtherOption: boolean;
  allowClear: boolean;
  dropdownValues: Array<string | { id: number | string; label: string }>;
}

export interface RecipeRowState {
  id?: number;
  symbol: string;
  name: string;
  comment: string;
  repeat?: number;
  endRepeat?: boolean;
  definedInTemplate: boolean;
  calcResultModified?: string[];
  formulas?: string[][];
  values: Map<string, string | number | string[]>;
  dropdownValues?: any[][];
  recipeGroupID?: number;

  /**
   * For rows created from startingMaterials (RowType.StartingMaterials),
   * stores the 1-based startingMaterial index.
   * Used for tracking which startingMaterial this row represents.
   */
  startingMaterialIndex?: number;

  /**
   * Optional human-readable row description (from template row definition).
   * Tooltip on the row label cell only ({@link Source.Constant} `name` / `symbol` column).
   */
  description?: string;

  // UI state
  showComment: boolean;
  scrollTo: boolean;
}

export interface RecipeGroupState {
  id?: number;
  name: string;
  color: string;
  symbol: string;
  rowType: RowType;
  scrolling: boolean;
  allowSeveral: boolean;
  copyLastRow: boolean;
  comments: CommentMode;
  regexFirstInclude?: string;
  regexFirstExclude?: string;
  filterColumn?: string;

  // Computed properties
  columns: RecipeColumnState[];
  rows: RecipeRowState[];
  canAddGroup: boolean;
  canRemoveGroup: boolean;
  commentColSpan: number;
  showAllComments: boolean;

  /**
   * Optional precomputed set of column symbols that should appear for this group.
   *
   * **Not part of the LiST API / recipe JSON today** — nothing in the pipeline, server parsers, or
   * `mapRecipeStateToDto` populates this. Normal visibility is derived at render time from column rules
   * (`visibility`, `refColumn`, `visibilityFormula`, row values).
   *
   * When present, {@link RecipeGroup} uses this as a fast path before live rule evaluation (readonly
   * plain data only). Intended for a future optional on-save optimization; legacy Knockout kept
   * `col.visible` in memory only and did not persist it either.
   */
  calculatedVisibility?: Set<string>;
}

export interface RecipeState {
  id?: number;
  templateName: string;
  templateHash?: string;
  groups: RecipeGroupState[];
  calculatedRuntime?: number;
  runtimeChangedManually: boolean;
}

// Formula scope interface
export interface FormulaScope {
  const: Record<string, any>;
  cell(rowSymbol: string, columnSymbol: string): string | number | null;
  duration(rowSymbol: string, columnSymbol: string, unit?: string): string | number | null;
  group(
    groupSymbol: string,
    columnSymbol: string,
    rowSymbol?: string
  ): string | number | null | Array<string | number | null>;
  lookup(field: string, column: string, rowSymbol?: string, groupSymbol?: string): string | number;
  uploadedValue(colHeader: string, keyColheader: string, rowKey: string): string | number;
  sumUploadedColumn(colHeader: string): number;
  evaluateFormula(
    formula: string,
    row: RecipeRowState
  ): string | number | boolean | null | Array<string | number | null>;
  getColumn(columnSymbol: string): RecipeColumnState | undefined;
  apply(object?: RecipeGroupState | RecipeRowState): FormulaScope;

  // Dependency tracking methods
  isColumnReferencedInFormulas(columnSymbol: string): boolean;
  isColumnReferencedInFormula(formula: string, columnSymbol: string): boolean;
  getFormulaDependencies(formula: string): string[];
  detectCircularDependencies(): { hasCircular: boolean; cycles: string[][] };
  getFormulaRelevantValues(formula: string, rowValues: Map<string, any>): Record<string, any>;
}

// Props interfaces
export interface RecipeProps {
  template: RecipeTemplateDto;
  data?: RecipeState;
  readonly?: boolean;
  onDataChange?: (data: RecipeState) => void;
  onRuntimeCalculated?: (runtime: number) => void;
}

export interface RecipeCellProps {
  row: RecipeRowState | import('./stores/RowStore').RowStore;
  column: RecipeColumnState;
  groupColor: string;
  groupSymbol?: string;
  readonly?: boolean;
  onValueChange: (value: string | number | string[]) => void;
  onArrowDownAtLastRow?: (columnIndex: number) => void;
  isTwoLine: boolean;
}
